import React, { useMemo } from 'react';
import { Select, Radio, Space, Typography, Alert, Descriptions, Tag, Spin } from 'antd';
import type { EsimBundle } from '../../services/adminService';
import {
  bundleLabel,
  groupBundlesByBalanceType,
  splitByFamily,
  balanceTypeSummary,
  formatValidity,
  isThrottled,
} from '../../esim/bundleHelpers';

const { Text, Paragraph } = Typography;

export type BundleFamily = 'travel-sim' | 'travel-zone';

interface BundlePickerProps {
  bundles: EsimBundle[];
  loading?: boolean;
  value?: string;
  onChange: (bundleId: string | undefined, bundle: EsimBundle | undefined) => void;
  family: BundleFamily;
  onFamilyChange: (f: BundleFamily) => void;
  // Top-up: throttled bundles cannot be added to a SIM that already has one.
  disableThrottled?: boolean;
  // Top-up: only the free-roaming family can be stacked; hide the family switch.
  hideFamilySwitch?: boolean;
}

// KAN-167: one picker for both the provisioning and the top-up flows. Bundles
// are split into two families and grouped by balance type inside each, with
// the selected bundle explained underneath in plain words.
const BundlePicker: React.FC<BundlePickerProps> = ({
  bundles,
  loading,
  value,
  onChange,
  family,
  onFamilyChange,
  disableThrottled,
  hideFamilySwitch,
}) => {
  const { travelSim, travelZone } = useMemo(() => splitByFamily(bundles), [bundles]);
  const list = family === 'travel-zone' ? travelZone : travelSim;
  const groups = useMemo(() => groupBundlesByBalanceType(list), [list]);
  const selected = bundles.find(b => b.id === value);

  const options = groups.map(g => ({
    label: `${g.label} — ${balanceTypeSummary({ key: g.key, label: g.label, description: g.description })}`,
    options: g.bundles.map(b => ({
      value: b.id,
      label: bundleLabel(b),
      disabled: !!disableThrottled && isThrottled(b.balanceType),
    })),
  }));

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {!hideFamilySwitch && (
        <div>
          <Radio.Group
            value={family}
            onChange={e => {
              onFamilyChange(e.target.value);
              onChange(undefined, undefined);
            }}
            optionType="button"
            buttonStyle="solid"
            options={[
              { value: 'travel-sim', label: `Travel SIM bundles (${travelSim.length})` },
              { value: 'travel-zone', label: `Travel Zone bundles (${travelZone.length})` },
            ]}
          />
          <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 13 }}>
            {family === 'travel-sim'
              ? 'Travel SIM bundles are the current catalogue: you pick the data amount and validity here, and the coverage (which countries) in the next step.'
              : 'Travel Zone bundles are the older packages where bundle and coverage come together. The number in the name is the coverage zone the package was sold for (Zone 1 = the cheapest set of countries, Zone 10 = the most expensive); Vodafone has not published which countries each zone contains. Nothing to choose in the coverage step. Prefer a Travel SIM bundle unless a customer specifically asks for a Travel Zone plan.'}
          </Paragraph>
        </div>
      )}
      <Select
        showSearch
        loading={loading}
        value={value}
        placeholder={
          loading ? 'Loading catalogue…' : 'Search by size or validity, e.g. "5GB" or "30D"'
        }
        optionFilterProp="label"
        allowClear
        style={{ width: '100%' }}
        options={options}
        onChange={id =>
          onChange(
            id,
            bundles.find(b => b.id === id)
          )
        }
        notFoundContent={loading ? <Spin size="small" /> : 'No bundles match'}
        aria-label="Bundle"
      />
      {selected && (
        <Descriptions size="small" bordered column={1}>
          <Descriptions.Item label="Bundle">
            {selected.name} <Text type="secondary">({selected.id})</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Data allowance">{selected.data || '—'}</Descriptions.Item>
          <Descriptions.Item label="Validity">
            {formatValidity(selected.validity)}{' '}
            <Text type="secondary">
              — counted from the first time the eSIM uses data, not from today
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="When the allowance is used up">
            <Tag color={isThrottled(selected.balanceType) ? 'orange' : 'blue'}>
              {selected.balanceType.label}
            </Tag>
            <div style={{ marginTop: 4 }}>{selected.balanceType.description}</div>
          </Descriptions.Item>
          {selected.family === 'travel-zone' && selected.defaultRoaming && (
            <Descriptions.Item label="Coverage">
              Fixed to {selected.name.replace(/\s+-\s+.*$/, '')} (roaming profile{' '}
              {selected.defaultRoaming.id})
            </Descriptions.Item>
          )}
        </Descriptions>
      )}
      {disableThrottled && groups.some(g => g.key.startsWith('throttled')) && (
        <Alert
          type="info"
          showIcon
          message="Throttled bundles are greyed out"
          description="Vodafone does not allow a throttled bundle to be added on top of another bundle. To move an eSIM to a throttled plan, wait until its current bundle expires, then provision the throttled bundle on its own."
        />
      )}
    </Space>
  );
};

export default BundlePicker;
